import { Fragment, useCallback, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { CheckCircle2, Copy, ExternalLink, Loader2, MessageCircle, RefreshCw, Send, Unplug } from 'lucide-react'
import { isAxiosError } from 'axios'
import { ClioMembresTemps, type ClioTimeError } from './ClioMembresTemps'
import { ClioTextosFactures } from './ClioTextosFactures'
import { api } from '@/lib/api'
import { createOrGetConversation, sendMessage, supportSession } from '@/lib/supportApi'
import { formatPhoneDisplay } from '@/lib/phoneValidation'
import { useProfile } from '@/api/profile'
import { Switch } from '@/components/ui/switch'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { usePlanRights } from '@/hooks/usePlanRights'
import { PlanBadge } from '@/components/ui/PlanBadge'

/**
 * Panneau d'un CRM (HubSpot, Pipedrive, Zoho CRM) dans le détail de l'intégration :
 * état de la connexion, bouton Connecter (OAuth), quatre réglages,
 * Déconnecter. Les deux CRM exposent la même API côté Callizy :
 * /api/companies/{id}/{crm}/ ; seuls les textes et la couleur changent.
 *
 * Le retour OAuth arrive avec ?{crm}=ok|refuse|erreur|portail-deja-lie :
 * on le dit une fois, puis on retire le paramètre de l'URL.
 */

export type Crm = 'hubspot' | 'pipedrive' | 'zoho' | 'google-contacts' | 'slack' | 'clio'

const COULEURS: Record<Crm, { fond: string; survol: string; ombre: string }> = {
  hubspot: { fond: 'bg-[#ff7a59]', survol: 'hover:bg-[#f0633f]', ombre: 'shadow-[#ff7a59]/20' },
  pipedrive: { fond: 'bg-[#017737]', survol: 'hover:bg-[#02602c]', ombre: 'shadow-[#017737]/20' },
  zoho: { fond: 'bg-[#e42527]', survol: 'hover:bg-[#c41f21]', ombre: 'shadow-[#e42527]/20' },
  'google-contacts': { fond: 'bg-[#4285f4]', survol: 'hover:bg-[#3367d6]', ombre: 'shadow-[#4285f4]/20' },
  slack: { fond: 'bg-[#4a154b]', survol: 'hover:bg-[#3b1040]', ombre: 'shadow-[#4a154b]/20' },
  clio: { fond: 'bg-[#0070e0]', survol: 'hover:bg-[#005bb5]', ombre: 'shadow-[#0070e0]/20' },
}
const TEXTE: Record<Crm, string> = { hubspot: 'text-[#ff7a59]', pipedrive: 'text-[#017737]', zoho: 'text-[#e42527]', 'google-contacts': 'text-[#4285f4]', slack: 'text-[#4a154b] dark:text-[#e01e5a]', clio: 'text-[#0070e0]' }
const INTERRUPTEUR: Record<Crm, string> = {
  hubspot: 'data-[state=checked]:bg-[#ff7a59]',
  pipedrive: 'data-[state=checked]:bg-[#017737]',
  zoho: 'data-[state=checked]:bg-[#e42527]',
  'google-contacts': 'data-[state=checked]:bg-[#4285f4]',
  slack: 'data-[state=checked]:bg-[#4a154b]',
  clio: 'data-[state=checked]:bg-[#0070e0]',
}
const NOMS: Record<Crm, string> = { hubspot: 'HubSpot', pipedrive: 'Pipedrive', zoho: 'Zoho CRM', 'google-contacts': 'Google Contacts', slack: 'Slack', clio: 'Clio' }
// Les clés i18n vivent sous manager.<clé> ; le tiret du chemin d'API devient un point sinon.
const CLE_I18N: Record<Crm, string> = { hubspot: 'hubspot', pipedrive: 'pipedrive', zoho: 'zoho', 'google-contacts': 'googleContacts', slack: 'slack', clio: 'clio' }
// Adresse que le bouton « Appeler avec Callizy » de Zoho ouvre : le softphone
// du dashboard. Le numéro s'ajoute avec le sélecteur de champ de Zoho, dont
// le jeton dépend de la langue du compte (${Contacts.Téléphone} en français).
const URL_BOUTON_ZOHO = 'https://app.callizy.ca/call?number='

interface EtatHubSpot {
  connected: boolean
  is_active?: boolean
  paused_by_plan?: boolean
  portal_id?: number | string
  portal_domain?: string
  crm_url?: string
  export_contacts?: boolean
  last_sync_at?: string | null
  notify_missed_calls?: boolean
  notify_voicemails?: boolean
  notify_sms?: boolean
  notify_completed_calls?: boolean
  create_time_entries?: boolean
  bill_sms?: boolean
  store_recordings?: boolean
  documents_refused?: boolean
  time_error?: ClioTimeError | null
  lines?: { number: string; label: string; synced: boolean }[]
  billing_increment_minutes?: number
  grow_token_set?: boolean
  grow_token_end?: string
  grow_leads_enabled?: boolean
  grow_last_error?: string
  region?: string
  hubspot_user_email?: string
  log_calls?: boolean
  log_sms?: boolean
  create_unknown_contacts?: boolean
  import_contacts?: boolean
  linked_contacts?: number
  last_event_at?: string | null
  last_error?: string
  connected_at?: string
}

type Reglage = 'log_calls' | 'log_sms' | 'create_unknown_contacts' | 'import_contacts' | 'export_contacts'
  | 'notify_missed_calls' | 'notify_voicemails' | 'notify_sms' | 'notify_completed_calls' | 'create_time_entries'
  | 'store_recordings' | 'bill_sms'
const REGLAGES_CRM: Reglage[] = ['log_calls', 'log_sms', 'create_unknown_contacts', 'import_contacts']
// Google Contacts n'a pas de journal d'appels : contacts dans les deux sens, et les appelants inconnus.
const REGLAGES_GOOGLE: Reglage[] = ['import_contacts', 'export_contacts', 'create_unknown_contacts']
// Slack : ce qu'on poste dans le canal.
const REGLAGES_SLACK: Reglage[] = ['notify_missed_calls', 'notify_voicemails', 'notify_sms', 'notify_completed_calls']
// Clio : les quatre des CRM, l'entrée de temps facturable par appel, la ligne facturée par texto,
// et les enregistrements dans le dossier.
const REGLAGES_CLIO: Reglage[] = [...REGLAGES_CRM, 'create_time_entries', 'bill_sms', 'store_recordings']
// Clio a un serveur par région ; la personne choisit le sien avant de se connecter.
// Pipedrive lets no other company install the Callizy app before it approves it ("App is not found",
// seen on 2 Nov. 2026). Until then the Connect button stays greyed and the client asks for activation:
// the request lands in support, and we send them the private app's installation link.
const ACTIVATION_SUR_DEMANDE: ReadonlySet<Crm> = new Set<Crm>(['pipedrive'])

// What the client gets, shown before they connect: the Callizy phone inside the CRM, the image of
// the integration's page on callizy.ca (fictitious data, an "-en" version for English screens).
const APERCUS: Partial<Record<Crm, string>> = { pipedrive: 'pipedrive-6', hubspot: 'hubspot-8' }

const REGIONS_CLIO = ['ca', 'us', 'eu', 'au'] as const
// Tranche de facturation des entrées de temps Clio, en minutes (arrondi à la tranche commencée).
const TRANCHES_CLIO = [6, 15, 1] as const

export function CrmPanel({ crm }: { crm: Crm }) {
  // HubSpot, Pipedrive, Zoho, Slack et Clio sont inclus à partir de Team ;
  // Google Contacts reste ouvert à tous.
  const { allowed, planFor } = usePlanRights()
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { data: profile } = useProfile()
  const companyId = profile?.company
  const base = `/api/companies/${companyId}/${crm}/`
  const couleur = COULEURS[crm]
  const tx = useCallback((cle: string, options?: Record<string, unknown>) => t(`manager.${CLE_I18N[crm]}.${cle}`, options), [t, crm])
  const reglages = crm === 'google-contacts' ? REGLAGES_GOOGLE : crm === 'slack' ? REGLAGES_SLACK : crm === 'clio' ? REGLAGES_CLIO : REGLAGES_CRM
  const [region, setRegion] = useState<(typeof REGIONS_CLIO)[number]>('ca')
  const [confirmerDeconnexion, setConfirmerDeconnexion] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: [crm, companyId],
    queryFn: async () => (await api.get<EtatHubSpot>(base)).data,
    enabled: Boolean(companyId),
  })
  // Clio est passé au forfait Team le 26 oct. 2026 : une entreprise déjà reliée garde sa connexion et peut la refaire.
  const crmPermis = crm === 'google-contacts' || allowed('crm_equipe') || (crm === 'clio' && !!data?.connected)

  // Message de retour OAuth, une seule fois
  useEffect(() => {
    const retour = searchParams.get(crm)
    if (!retour) return
    if (retour === 'ok') toast.success(tx('retour.ok'))
    else if (retour === 'refuse') toast.error(tx('retour.refuse'))
    else if (retour === 'portail-deja-lie') toast.error(tx('retour.portailDejaLie'))
    else toast.error(tx('retour.erreur'))
    const next = new URLSearchParams(searchParams)
    next.delete(crm)
    setSearchParams(next, { replace: true })
    queryClient.invalidateQueries({ queryKey: [crm, companyId] })
  }, [searchParams, setSearchParams, tx, queryClient, companyId, crm])

  // Activation on request: the request is a support message, remembered on this browser so it is sent once.
  const surDemande = ACTIVATION_SUR_DEMANDE.has(crm)
  const cleDemande = `callizy_activation_${crm}_${companyId ?? ''}`
  const [demandeEnvoyee, setDemandeEnvoyee] = useState(false)
  useEffect(() => {
    try {
      setDemandeEnvoyee(localStorage.getItem(cleDemande) === '1')
    } catch {
      setDemandeEnvoyee(false)
    }
  }, [cleDemande])
  const demanderActivation = useMutation({
    mutationFn: async () => {
      const conversation = await createOrGetConversation({})
      const jeton = conversation.session_token ?? supportSession.get()?.token
      if (!jeton) throw new Error('support')
      await sendMessage(conversation.id, jeton, tx('activation.message'))
    },
    onSuccess: () => {
      try {
        localStorage.setItem(cleDemande, '1')
      } catch {
        // Private browsing: the request is sent all the same.
      }
      setDemandeEnvoyee(true)
      toast.success(tx('activation.envoyee'))
    },
    onError: () => toast.error(tx('activation.erreur')),
  })

  const connecter = useMutation({
    mutationFn: async () => (await api.post<{ url: string }>(`${base}connect/`, crm === 'clio' ? { locale, region } : { locale })).data,
    onSuccess: ({ url }) => {
      window.location.href = url
    },
    onError: () => toast.error(tx('retour.erreur')),
  })

  const regler = useMutation({
    mutationFn: async (champs: Partial<Record<Reglage, boolean>> & { billing_increment_minutes?: number; excluded_lines?: string[] }) =>
      (await api.patch<EtatHubSpot>(`${base}settings/`, champs)).data,
    onSuccess: (etat) => queryClient.setQueryData([crm, companyId], etat),
    onError: () => toast.error(tx('retour.erreur')),
  })

  // Clio Grow: lead inbox token and the "send new callers" switch. The token never comes back in clear.
  const [jetonGrow, setJetonGrow] = useState('')
  const reglerGrow = useMutation({
    mutationFn: async (champs: { grow_token?: string; grow_leads_enabled?: boolean }) =>
      (await api.patch<EtatHubSpot>(`${base}grow/`, champs)).data,
    onSuccess: (etat, champs) => {
      queryClient.setQueryData([crm, companyId], etat)
      if (champs.grow_token) {
        setJetonGrow('')
        toast.success(tx('grow.jetonSauve'))
      }
    },
    onError: () => toast.error(tx('retour.erreur')),
  })
  const essaiGrow = useMutation({
    mutationFn: async () => (await api.post<EtatHubSpot & { sent: boolean }>(`${base}grow/test/`)).data,
    onSuccess: (etat) => {
      queryClient.setQueryData([crm, companyId], etat)
      toast.success(tx('grow.essaiEnvoye'))
    },
    onError: (erreur) => {
      const corps = isAxiosError(erreur) ? (erreur.response?.data as (EtatHubSpot & { error?: string }) | undefined) : undefined
      if (corps?.connected) queryClient.setQueryData([crm, companyId], corps)
      toast.error(tx(`grow.erreurs.${corps?.error === 'token_refused' ? 'token_refused' : corps?.error === 'lead_refused' ? 'lead_refused' : 'autre'}`))
    },
  })

  const synchroniser = useMutation({
    mutationFn: async () => (await api.post(`${base}sync/`)).data,
    onSuccess: () => toast.success(tx('syncLancee')),
    onError: () => toast.error(tx('retour.erreur')),
  })

  const deconnecter = useMutation({
    mutationFn: async () => (await api.delete(`${base}disconnect/`)).data,
    onSuccess: () => {
      queryClient.setQueryData([crm, companyId], { connected: false })
      toast.success(tx('deconnecte'))
    },
    onError: () => toast.error(tx('retour.erreur')),
  })

  if (isLoading || !data) {
    return (
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 flex items-center gap-3 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        {tx('chargement')}
      </div>
    )
  }

  if (!data.connected) {
    return (
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm space-y-4">
        {/* The image says what the connection brings; the header and "How it works" above say the rest. */}
        {APERCUS[crm] ? (
          <figure className="space-y-2.5">
            <img
              src={`https://callizy.ca/images/integrations/${APERCUS[crm]}${locale === 'en' ? '-en' : ''}.jpg`}
              alt={tx('apercu.alt')}
              loading="lazy"
              className="w-full rounded-xl border border-slate-200 dark:border-slate-700"
            />
            <figcaption className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{tx('apercu.legende')}</figcaption>
          </figure>
        ) : (
          !surDemande && <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{tx('pasConnecte')}</p>
        )}
        {crm === 'clio' && (
          <label className="block">
            <span className="block text-sm font-semibold text-slate-900 dark:text-white mb-1.5">{tx('region.titre')}</span>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value as (typeof REGIONS_CLIO)[number])}
              className="w-full max-w-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm text-slate-900 dark:text-white"
            >
              {REGIONS_CLIO.map((r) => <option key={r} value={r}>{tx(`region.${r}`)}</option>)}
            </select>
            <span className="block text-xs text-slate-500 dark:text-slate-400 mt-1.5">{tx('region.aide')}</span>
          </label>
        )}
        {!crmPermis && <PlanBadge plan={planFor('crm_equipe')} className="mb-3" />}
        {surDemande && <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{tx('activation.explication')}</p>}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => connecter.mutate()}
            disabled={surDemande || !crmPermis || connecter.isPending}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl ${couleur.fond} text-white text-sm font-bold ${couleur.survol} transition-colors shadow-lg ${couleur.ombre} disabled:opacity-60 ${surDemande ? 'disabled:bg-slate-200 disabled:text-slate-500 disabled:opacity-100 disabled:shadow-none dark:disabled:bg-slate-800 dark:disabled:text-slate-400' : ''}`}
          >
            {connecter.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
            {tx('connecter')}
          </button>
          {surDemande && (
            <button
              type="button"
              onClick={() => demanderActivation.mutate()}
              disabled={!crmPermis || demandeEnvoyee || demanderActivation.isPending}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-60"
            >
              {demanderActivation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : demandeEnvoyee ? <CheckCircle2 className="w-4 h-4" /> : <MessageCircle className="w-4 h-4" />}
              {demandeEnvoyee ? tx('activation.demandee') : tx('activation.demander')}
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 p-5 flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
        <div className="min-w-0 text-sm">
          <p className="font-semibold text-slate-900 dark:text-white">
            {tx('connecteA', { portail: data.portal_domain || data.portal_id })}
          </p>
          <p className="text-slate-600 dark:text-slate-300 mt-0.5">
            {data.hubspot_user_email ? tx('par', { courriel: data.hubspot_user_email }) : null}
            {typeof data.linked_contacts === 'number'
              ? ` · ${tx('contactsLies', { n: data.linked_contacts })}`
              : null}
          </p>
          {/* Clio: access removed by the firm leaves no error text, the cut alone is shown */}
          {!data.is_active && (data.last_error || crm === 'clio') ? (
            <p className="mt-2 text-red-600 dark:text-red-400">{tx('coupee')} {data.last_error}</p>
          ) : null}
        </div>
      </div>

      {/* Connected during a trial or on an older plan: the server sends nothing outside Team */}
      {data.paused_by_plan && (
        <div className="rounded-2xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-5 text-sm">
          <p className="text-slate-700 dark:text-slate-200 leading-relaxed">{t('manager.crmEnPause', { nom: NOMS[crm] })}</p>
          <PlanBadge plan={planFor('crm_equipe')} className="mt-3" />
        </div>
      )}

      {/* The Callizy phone in HubSpot's Call button: each user picks it in HubSpot */}
      {crm === 'hubspot' && (
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm">
        <p className="text-sm font-semibold text-slate-900 dark:text-white">{tx('telephone.titre')}</p>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{tx('telephone.texte')}</p>
      </div>
      )}

      {/* Optional record card (texting from the record): a HubSpot admin adds it once per portal.
          Without a portal id, HubSpot's account-agnostic /l/ link opens the page in the user's portal. */}
      {crm === 'hubspot' && (
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm">
        <p className="text-sm font-semibold text-slate-900 dark:text-white">{tx('carte.titre')}</p>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{tx('carte.texte')}</p>
        <a
          href={data.portal_id
            ? `https://app.hubspot.com/object-type-settings/${data.portal_id}/object/0-1`
            : 'https://app.hubspot.com/l/object-type-settings/object/0-1'}
          target="_blank"
          rel="noopener noreferrer"
          className={`mt-3 inline-flex items-center gap-2 text-sm font-semibold hover:underline ${TEXTE[crm]}`}
        >
          {tx('carte.lien')}
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
      )}

      {/* Le bouton d'appel sur la fiche : Zoho n'a pas d'extension déclarée, l'admin crée un bouton avec cette adresse */}
      {crm === 'zoho' && (
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm">
        <p className="text-sm font-semibold text-slate-900 dark:text-white">{tx('carte.titre')}</p>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{tx('carte.texte')}</p>
        <div className="mt-3 flex items-center gap-2">
          <code className="flex-1 min-w-0 truncate rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-2 text-xs text-slate-800 dark:text-slate-200">
            {URL_BOUTON_ZOHO}
          </code>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(URL_BOUTON_ZOHO).then(() => toast.success(t('manager.marketplace.mcp.copied')))
            }}
            className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <Copy className="w-3.5 h-3.5" />
            {t('manager.marketplace.mcp.copy')}
          </button>
        </div>
        {data.crm_url ? (
          <a
            href={`${data.crm_url}/crm/settings/modules`}
            target="_blank"
            rel="noopener noreferrer"
            className={`mt-3 inline-flex items-center gap-2 text-sm font-semibold hover:underline ${TEXTE[crm]}`}
          >
            {tx('carte.lien')}
            <ExternalLink className="w-4 h-4" />
          </a>
        ) : null}
      </div>
      )}

      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm divide-y divide-slate-100 dark:divide-slate-800">
        {reglages.map((cle) => (
          <Fragment key={cle}>
            <label className="flex items-center justify-between gap-4 px-6 py-4 cursor-pointer">
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-900 dark:text-white">
                  {tx(`reglages.${cle}.titre`)}
                </span>
                <span className="block text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  {tx(`reglages.${cle}.texte`)}
                </span>
                {/* Clio refused the documents: the app lacks this right until Clio is reconnected */}
                {cle === 'store_recordings' && data.store_recordings && data.documents_refused ? (
                  <span className="block text-sm text-amber-700 dark:text-amber-400 mt-1">{tx('reglages.store_recordings.reconnecter')}</span>
                ) : null}
                {/* Texts are billed on the line of their communication: no texts in Clio, no line */}
                {cle === 'bill_sms' && !data.log_sms ? (
                  <span className="block text-sm text-amber-700 dark:text-amber-400 mt-1">{tx('reglages.bill_sms.sansTextos')}</span>
                ) : null}
              </span>
              <Switch
                checked={Boolean(data[cle])}
                onCheckedChange={(valeur) => regler.mutate({ [cle]: valeur })}
                disabled={cle === 'bill_sms' && !data.log_sms && !data.bill_sms}
                className={INTERRUPTEUR[crm]}
              />
            </label>
            {/* Clio : la tranche à laquelle le temps facturé est arrondi, seulement si les entrées de temps sont cochées */}
            {cle === 'create_time_entries' && data.create_time_entries ? (
              <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 px-6 py-4">
                <span className="text-sm font-semibold text-slate-900 dark:text-white">{tx('arrondi.titre')}</span>
                <select
                  value={data.billing_increment_minutes ?? 6}
                  onChange={(e) => regler.mutate({ billing_increment_minutes: Number(e.target.value) })}
                  disabled={regler.isPending}
                  className="w-full sm:w-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm text-slate-900 dark:text-white disabled:opacity-60"
                >
                  {TRANCHES_CLIO.map((m) => <option key={m} value={m}>{tx(`arrondi.${m}`)}</option>)}
                </select>
              </label>
            ) : null}
            {/* Clio: whose Clio account each member's call time goes to */}
            {cle === 'create_time_entries' && data.create_time_entries ? (
              <ClioMembresTemps companyId={companyId} timeError={data.time_error} />
            ) : null}
            {/* Clio: the flat rate category of a received text and of a sent text */}
            {cle === 'bill_sms' && data.bill_sms ? <ClioTextosFactures companyId={companyId} /> : null}
          </Fragment>
        ))}
      </div>

      {/* Clio: the company lines whose calls, voicemails and texts go to Clio (all checked by default) */}
      {crm === 'clio' && data.lines && data.lines.length > 0 ? (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <div className="px-6 pt-4">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">{tx('lignes.titre')}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{tx('lignes.texte')}</p>
          </div>
          <ul className="px-6 py-1 divide-y divide-slate-100 dark:divide-slate-800">
            {data.lines.map((ligne) => (
              <li key={ligne.number}>
                <label className="flex items-center gap-3 py-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ligne.synced}
                    disabled={regler.isPending}
                    onChange={(e) => {
                      const excluded = (data.lines ?? [])
                        .filter((l) => (l.number === ligne.number ? !e.target.checked : !l.synced))
                        .map((l) => l.number)
                      regler.mutate({ excluded_lines: excluded })
                    }}
                    className="h-5 w-5 flex-shrink-0 rounded accent-[#0070e0]"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-900 dark:text-white truncate">
                      {ligne.label || formatPhoneDisplay(ligne.number)}
                    </span>
                    {ligne.label ? (
                      <span className="block text-xs text-slate-500 dark:text-slate-400">{formatPhoneDisplay(ligne.number)}</span>
                    ) : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Clio Grow: new callers answered by the AI agent become leads in the Grow lead inbox */}
      {crm === 'clio' && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm divide-y divide-slate-100 dark:divide-slate-800">
          <div className="px-6 py-4 space-y-3">
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">{tx('grow.titre')}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{tx('grow.texte')}</p>
            </div>
            {data.grow_token_set ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  {data.grow_token_end ? tx('grow.jetonEnregistre', { fin: data.grow_token_end }) : tx('grow.jetonEnregistreSansFin')}
                </span>
                <button
                  type="button"
                  onClick={() => reglerGrow.mutate({ grow_token: '' })}
                  disabled={reglerGrow.isPending}
                  className="self-start sm:self-auto text-sm font-semibold text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-60"
                >
                  {tx('grow.retirer')}
                </button>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (jetonGrow.trim()) reglerGrow.mutate({ grow_token: jetonGrow.trim() })
                }}
              >
                <label htmlFor="jeton-grow" className="block text-sm font-semibold text-slate-900 dark:text-white mb-1.5">
                  {tx('grow.jeton')}
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    id="jeton-grow"
                    type="password"
                    autoComplete="off"
                    value={jetonGrow}
                    onChange={(e) => setJetonGrow(e.target.value)}
                    placeholder={tx('grow.placeholder')}
                    className="w-full sm:flex-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-base sm:text-sm text-slate-900 dark:text-white"
                  />
                  <button
                    type="submit"
                    disabled={!jetonGrow.trim() || reglerGrow.isPending}
                    className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl ${couleur.fond} text-white text-sm font-bold ${couleur.survol} transition-colors disabled:opacity-60`}
                  >
                    {reglerGrow.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {tx('grow.enregistrer')}
                  </button>
                </div>
                <span className="block text-xs text-slate-500 dark:text-slate-400 mt-1.5">{tx('grow.ouTrouver')}</span>
              </form>
            )}
          </div>
          <label className={`flex items-center justify-between gap-4 px-6 py-4 ${data.grow_token_set ? 'cursor-pointer' : 'opacity-60'}`}>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-slate-900 dark:text-white">{tx('grow.envoyer')}</span>
              <span className="block text-sm text-slate-500 dark:text-slate-400 mt-0.5">{tx('grow.envoyerTexte')}</span>
            </span>
            <Switch
              checked={Boolean(data.grow_leads_enabled)}
              disabled={!data.grow_token_set || reglerGrow.isPending}
              onCheckedChange={(valeur) => reglerGrow.mutate({ grow_leads_enabled: valeur })}
              className={INTERRUPTEUR[crm]}
            />
          </label>
          <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <button
              type="button"
              onClick={() => essaiGrow.mutate()}
              disabled={!data.grow_token_set || essaiGrow.isPending}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-60"
            >
              {essaiGrow.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {tx('grow.essai')}
            </button>
            {data.grow_last_error ? (
              <p className="text-sm text-red-600 dark:text-red-400">
                {tx(`grow.erreurs.${data.grow_last_error === 'token_refused' ? 'token_refused' : 'lead_refused'}`)}
              </p>
            ) : null}
          </div>
        </div>
      )}

      {crm === 'google-contacts' && data.is_active ? (
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={() => synchroniser.mutate()}
            disabled={synchroniser.isPending}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-60"
          >
            {synchroniser.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {tx('synchroniser')}
          </button>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {data.last_sync_at ? tx('derniereSync', { quand: new Date(data.last_sync_at).toLocaleString(locale) }) : tx('jamaisSync')}
          </span>
        </div>
      ) : null}

      {!data.is_active ? (
        <button
          type="button"
          onClick={() => connecter.mutate()}
          disabled={!crmPermis}
          className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl ${couleur.fond} text-white text-sm font-bold ${couleur.survol} transition-colors disabled:opacity-60`}
        >
          <ExternalLink className="w-4 h-4" />
          {tx('reconnecter')}
        </button>
      ) : null}

      <button
        type="button"
        onClick={() => setConfirmerDeconnexion(true)}
        disabled={deconnecter.isPending}
        className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
      >
        <Unplug className="w-4 h-4" />
        {tx('deconnecter')}
      </button>

      <AlertDialog open={confirmerDeconnexion} onOpenChange={(ouvert) => !deconnecter.isPending && setConfirmerDeconnexion(ouvert)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tx('deconnecter')}</AlertDialogTitle>
            <AlertDialogDescription>{tx('confirmerDeconnexion')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deconnecter.isPending}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                deconnecter.mutate(undefined, { onSettled: () => setConfirmerDeconnexion(false) })
              }}
              disabled={deconnecter.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deconnecter.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : tx('deconnecter')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
